import React, { useState, useEffect, useCallback } from 'react';
import {
    Sheet,
    Typography,
    Card,
    CardContent,
    Stack,
    Button,
    IconButton,
    Modal,
    ModalDialog,
    ModalOverflow,
    ModalClose,
    FormControl,
    FormLabel,
    Input,
    Select,
    Option,
    LinearProgress,
    Alert,
    Divider,
    Chip,
    Snackbar
} from '@mui/joy';
import createApi, {
    getAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    transferBetweenAccounts,
    reconcileAccount
} from '../api';

const AccountsTab = ({ initDataRaw }) => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Диалоги
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
    
    // Выбранный счёт для редактирования
    const [selectedAccount, setSelectedAccount] = useState(null);
    
    // Форма создания
    const [newAccountName, setNewAccountName] = useState('');
    const [newAccountType, setNewAccountType] = useState('everyday');
    const [newAccountBalance, setNewAccountBalance] = useState('');
    
    // Форма редактирования имени
    const [editName, setEditName] = useState('');
    
    // Форма сверки баланса с реальностью
    const [reconcileActual, setReconcileActual] = useState('');

    // Тост об успешной сверке
    const [snack, setSnack] = useState('');
    
    // Форма перевода
    const [transferFrom, setTransferFrom] = useState('');
    const [transferTo, setTransferTo] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [transferDescription, setTransferDescription] = useState('');
    
    // Состояние операций
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const api = createApi(initDataRaw);

    const fetchAccounts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAccounts(api);
            setAccounts(response.data);
            setError(null);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching accounts:', err);
        } finally {
            setLoading(false);
        }
    }, [initDataRaw]);

    useEffect(() => {
        if (initDataRaw) {
            fetchAccounts();
        }
    }, [initDataRaw, fetchAccounts]);

    // Получить отображаемое имя счёта
    const getAccountDisplayName = (name) => {
        const names = {
            'family': 'Семейный счёт',
            'savings': 'Накопительный счёт'
        };
        return names[name] || name;
    };

    // Получить тип счёта для отображения
    const getAccountTypeLabel = (type) => {
        const types = {
            'everyday': 'Повседневный',
            'savings': 'Накопительный'
        };
        return types[type] || type;
    };

    // Создание счёта
    const handleCreate = async () => {
        if (!newAccountName.trim()) {
            setFormError('Введите название счёта');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            await createAccount(api, {
                name: newAccountName.trim(),
                type: newAccountType,
                balance: parseFloat(newAccountBalance) || 0
            });
            
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            setCreateDialogOpen(false);
            setNewAccountName('');
            setNewAccountType('everyday');
            setNewAccountBalance('');
            fetchAccounts();
        } catch (err) {
            setFormError(err.response?.data?.error || 'Ошибка при создании счёта');
        } finally {
            setSubmitting(false);
        }
    };

    // Редактирование имени
    const handleEdit = async () => {
        if (!editName.trim()) {
            setFormError('Введите название счёта');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            await updateAccount(api, selectedAccount.id, { name: editName.trim() });
            
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            setEditDialogOpen(false);
            setEditName('');
            setSelectedAccount(null);
            fetchAccounts();
        } catch (err) {
            setFormError(err.response?.data?.error || 'Ошибка при обновлении счёта');
        } finally {
            setSubmitting(false);
        }
    };

    // Сверка баланса с реальностью
    const handleReconcile = async () => {
        const actual = parseFloat(reconcileActual);
        if (isNaN(actual)) {
            setFormError('Введите фактический баланс');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            const res = await reconcileAccount(api, selectedAccount.id, {
                actual_balance: actual,
                note: 'Сверка в приложении'
            });

            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            setReconcileDialogOpen(false);
            setReconcileActual('');
            setSelectedAccount(null);
            fetchAccounts();

            const delta = res.data?.delta ?? 0;
            if (delta === 0) {
                setSnack('Всё уже сходится 👍');
            } else {
                const sign = delta > 0 ? '+' : '−';
                setSnack(`Свели. Записали корректировку ${sign}${Math.abs(delta).toLocaleString('ru-RU')} ₽ — теперь всё сходится.`);
            }
        } catch (err) {
            setFormError(err.response?.data?.error || 'Ошибка при сверке');
        } finally {
            setSubmitting(false);
        }
    };

    // Удаление счёта
    const handleDelete = async (account) => {
        if (!window.confirm(`Удалить счёт "${getAccountDisplayName(account.name)}"?`)) {
            return;
        }

        try {
            await deleteAccount(api, account.id);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            fetchAccounts();
        } catch (err) {
            alert(err.response?.data?.error || 'Ошибка при удалении счёта');
        }
    };

    // Перевод между счетами
    const handleTransfer = async () => {
        if (!transferFrom || !transferTo) {
            setFormError('Выберите счета');
            return;
        }

        const amount = parseFloat(transferAmount);
        if (isNaN(amount) || amount <= 0) {
            setFormError('Введите корректную сумму');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            await transferBetweenAccounts(api, {
                from_account: transferFrom,
                to_account: transferTo,
                amount,
                description: transferDescription
            });
            
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
            setTransferDialogOpen(false);
            setTransferFrom('');
            setTransferTo('');
            setTransferAmount('');
            setTransferDescription('');
            fetchAccounts();
        } catch (err) {
            setFormError(err.response?.data?.error || 'Ошибка при переводе');
        } finally {
            setSubmitting(false);
        }
    };

    // Открыть диалог редактирования
    const openEditDialog = (account) => {
        setSelectedAccount(account);
        setEditName(account.name);
        setFormError('');
        setEditDialogOpen(true);
    };

    // Открыть диалог сверки. Поле намеренно пустое — чтобы вводили
    // фактический баланс из банка, а не правили старое число.
    const openReconcileDialog = (account) => {
        setSelectedAccount(account);
        setReconcileActual('');
        setFormError('');
        setReconcileDialogOpen(true);
    };

    // Открыть диалог перевода с предустановленными значениями
    const openTransferDialog = (fromAccount = '', toAccount = '') => {
        setTransferFrom(fromAccount);
        setTransferTo(toAccount);
        setTransferAmount('');
        setTransferDescription('');
        setFormError('');
        setTransferDialogOpen(true);
    };

    if (loading) return <LinearProgress />;
    if (error) return <Typography color="danger">Ошибка: {error}</Typography>;

    const savingsAccount = accounts.find(a => a.name === 'savings');
    const familyAccount = accounts.find(a => a.name === 'family');

    return (
        <Sheet sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">Счета</Typography>
                    <Button 
                        size="sm" 
                        onClick={() => {
                            setFormError('');
                            setCreateDialogOpen(true);
                        }}
                    >
                        + Создать
                    </Button>
                </Stack>

                {/* Быстрый перевод с накопительного на семейный */}
                {savingsAccount && familyAccount && savingsAccount.balance > 0 && (
                    <Alert 
                        color="primary" 
                        variant="soft"
                        endDecorator={
                            <Button 
                                size="sm" 
                                variant="solid" 
                                color="primary"
                                onClick={() => openTransferDialog('savings', 'family')}
                            >
                                Перевести
                            </Button>
                        }
                    >
                        <Typography level="body-sm">
                            На накопительном счёте: <strong>{savingsAccount.balance.toLocaleString('ru-RU')} ₽</strong>
                        </Typography>
                    </Alert>
                )}

                {/* Список счетов */}
                {accounts.map((account) => (
                    <Card key={account.id} variant="outlined">
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Stack spacing={0.5}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography level="title-md">
                                            {getAccountDisplayName(account.name)}
                                        </Typography>
                                        {account.is_default && (
                                            <Chip size="sm" color="primary" variant="soft">
                                                По умолчанию
                                            </Chip>
                                        )}
                                    </Stack>
                                    <Typography level="body-sm" color="neutral">
                                        {getAccountTypeLabel(account.type)}
                                    </Typography>
                                </Stack>
                                <Typography level="h4" color={account.balance >= 0 ? 'success' : 'danger'}>
                                    {account.balance.toLocaleString('ru-RU')} ₽
                                </Typography>
                            </Stack>
                            
                            <Divider sx={{ my: 1.5 }} />
                            
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    size="sm"
                                    variant="plain"
                                    onClick={() => openReconcileDialog(account)}
                                >
                                    Сверить
                                </Button>
                                <Button 
                                    size="sm" 
                                    variant="plain"
                                    onClick={() => openEditDialog(account)}
                                >
                                    Переименовать
                                </Button>
                                {!account.is_default && account.name !== 'family' && account.name !== 'savings' && (
                                    <Button 
                                        size="sm" 
                                        variant="plain" 
                                        color="danger"
                                        onClick={() => handleDelete(account)}
                                    >
                                        Удалить
                                    </Button>
                                )}
                                {accounts.length > 1 && (
                                    <Button 
                                        size="sm" 
                                        variant="soft"
                                        onClick={() => openTransferDialog(account.name, '')}
                                    >
                                        Перевести
                                    </Button>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                ))}

                {accounts.length === 0 && (
                    <Typography level="body-md" textAlign="center" sx={{ py: 4 }}>
                        Нет счетов
                    </Typography>
                )}
            </Stack>

            {/* Диалог создания счёта */}
            <Modal open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
                <ModalOverflow>
                <ModalDialog sx={{
                    width: '90%',
                    maxWidth: 400,
                    pb: 'env(safe-area-inset-bottom, 16px)'
                }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>Создать счёт</Typography>
                    
                    {formError && (
                        <Alert color="danger" sx={{ mb: 2 }}>{formError}</Alert>
                    )}
                    
                    <Stack spacing={2}>
                        <FormControl required>
                            <FormLabel>Название</FormLabel>
                            <Input
                                placeholder="Например: Подушка безопасности"
                                value={newAccountName}
                                onChange={(e) => setNewAccountName(e.target.value)}
                            />
                        </FormControl>
                        
                        <FormControl>
                            <FormLabel>Тип</FormLabel>
                            <Select
                                value={newAccountType}
                                onChange={(e, val) => setNewAccountType(val)}
                            >
                                <Option value="everyday">Повседневный</Option>
                                <Option value="savings">Накопительный</Option>
                            </Select>
                        </FormControl>
                        
                        <FormControl>
                            <FormLabel>Начальный баланс</FormLabel>
                            <Input
                                type="number"
                                placeholder="0"
                                value={newAccountBalance}
                                onChange={(e) => setNewAccountBalance(e.target.value)}
                                endDecorator="₽"
                            />
                        </FormControl>
                        
                        <Button onClick={handleCreate} loading={submitting}>
                            Создать
                        </Button>
                    </Stack>
                </ModalDialog>
                </ModalOverflow>
            </Modal>

            {/* Диалог редактирования имени */}
            <Modal open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <ModalOverflow>
                <ModalDialog sx={{
                    width: '90%',
                    maxWidth: 400,
                    pb: 'env(safe-area-inset-bottom, 16px)'
                }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>Переименовать счёт</Typography>
                    
                    {formError && (
                        <Alert color="danger" sx={{ mb: 2 }}>{formError}</Alert>
                    )}
                    
                    <Stack spacing={2}>
                        <FormControl required>
                            <FormLabel>Новое название</FormLabel>
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                            />
                        </FormControl>
                        
                        <Button onClick={handleEdit} loading={submitting}>
                            Сохранить
                        </Button>
                    </Stack>
                </ModalDialog>
                </ModalOverflow>
            </Modal>

            {/* Диалог сверки баланса с реальностью */}
            <Modal open={reconcileDialogOpen} onClose={() => setReconcileDialogOpen(false)}>
                <ModalOverflow>
                <ModalDialog sx={{
                    width: '90%',
                    maxWidth: 400,
                    pb: 'env(safe-area-inset-bottom, 16px)'
                }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 0.5 }}>Сверка с реальностью</Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                        {selectedAccount && getAccountDisplayName(selectedAccount.name)}
                    </Typography>

                    {formError && (
                        <Alert color="danger" sx={{ mb: 2 }}>{formError}</Alert>
                    )}

                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography level="body-sm" color="neutral">В приложении сейчас</Typography>
                            <Typography level="title-sm">
                                {selectedAccount && selectedAccount.balance.toLocaleString('ru-RU')} ₽
                            </Typography>
                        </Stack>

                        <FormControl required>
                            <FormLabel>Реальный баланс (из банка)</FormLabel>
                            <Input
                                type="number"
                                placeholder="Введите фактическую сумму"
                                value={reconcileActual}
                                onChange={(e) => setReconcileActual(e.target.value)}
                                endDecorator="₽"
                                autoFocus
                            />
                        </FormControl>

                        {/* Live-предпросмотр разницы */}
                        {selectedAccount && reconcileActual !== '' && !isNaN(parseFloat(reconcileActual)) && (() => {
                            const delta = Math.round((parseFloat(reconcileActual) - selectedAccount.balance) * 100) / 100;
                            if (delta === 0) {
                                return (
                                    <Alert color="success" variant="soft">
                                        Всё сходится — корректировка не нужна.
                                    </Alert>
                                );
                            }
                            const sign = delta > 0 ? '+' : '−';
                            const isFamily = selectedAccount.name === 'family';
                            return (
                                <Alert color="primary" variant="soft">
                                    <Typography level="body-sm">
                                        Разница: <strong>{sign}{Math.abs(delta).toLocaleString('ru-RU')} ₽</strong>
                                        {isFamily ? ' будет записана как «Корректировка» за сегодня.' : '.'}
                                    </Typography>
                                </Alert>
                            );
                        })()}

                        <Typography level="body-xs" color="neutral">
                            Небольшие расхождения — это норма (наличные, забытая покупка).
                            Введите фактическую сумму — остальное подравняем.
                        </Typography>

                        <Button onClick={handleReconcile} loading={submitting}>
                            Свести
                        </Button>
                    </Stack>
                </ModalDialog>
                </ModalOverflow>
            </Modal>

            {/* Тост об успешной сверке */}
            <Snackbar
                open={!!snack}
                onClose={() => setSnack('')}
                autoHideDuration={4000}
                color="success"
                variant="soft"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snack}
            </Snackbar>

            {/* Диалог перевода */}
            <Modal open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)}>
                <ModalOverflow>
                <ModalDialog sx={{
                    width: '90%',
                    maxWidth: 400,
                    pb: 'env(safe-area-inset-bottom, 16px)'
                }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>Перевод между счетами</Typography>
                    
                    {formError && (
                        <Alert color="danger" sx={{ mb: 2 }}>{formError}</Alert>
                    )}
                    
                    <Stack spacing={2}>
                        <FormControl required>
                            <FormLabel>Со счёта</FormLabel>
                            <Select
                                placeholder="Выберите счёт"
                                value={transferFrom}
                                onChange={(e, val) => setTransferFrom(val)}
                            >
                                {accounts.map(acc => (
                                    <Option key={acc.id} value={acc.name} disabled={acc.name === transferTo}>
                                        {getAccountDisplayName(acc.name)} ({acc.balance.toLocaleString('ru-RU')} ₽)
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FormControl required>
                            <FormLabel>На счёт</FormLabel>
                            <Select
                                placeholder="Выберите счёт"
                                value={transferTo}
                                onChange={(e, val) => setTransferTo(val)}
                            >
                                {accounts.map(acc => (
                                    <Option key={acc.id} value={acc.name} disabled={acc.name === transferFrom}>
                                        {getAccountDisplayName(acc.name)}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FormControl required>
                            <FormLabel>Сумма</FormLabel>
                            <Input
                                type="number"
                                placeholder="0"
                                value={transferAmount}
                                onChange={(e) => setTransferAmount(e.target.value)}
                                endDecorator="₽"
                            />
                        </FormControl>
                        
                        <FormControl>
                            <FormLabel>Комментарий</FormLabel>
                            <Input
                                placeholder="Необязательно"
                                value={transferDescription}
                                onChange={(e) => setTransferDescription(e.target.value)}
                            />
                        </FormControl>
                        
                        <Button onClick={handleTransfer} loading={submitting} color="primary">
                            Перевести
                        </Button>
                    </Stack>
                </ModalDialog>
                </ModalOverflow>
            </Modal>
        </Sheet>
    );
};

export default AccountsTab;
